import { useEffect, useRef, useState, type PointerEvent } from "react";
import { Icon } from "../icons/Icon";
import { clamp } from "./utils";
import { cn } from "@/lib/styles.utils";

type Props = {
  urls: string[];
  startIndex?: number;
  loop?: boolean;
  showIndicators?: boolean;
  className?: string;
  panelClassName?: string;
  onIndexChange?: (i: number) => void;
};

const Carousel = ({
  urls,
  startIndex = 0,
  loop = true,
  showIndicators = true,
  className = "",
  panelClassName = "h-72 md:h-96",
  onIndexChange,
}: Props) => {
  const [i, setI] = useState(() =>
    clamp(startIndex, 0, Math.max(0, urls?.length - 1)),
  );
  const trackRef = useRef<HTMLDivElement | null>(null);
  const startX = useRef<number | null>(null);
  const deltaX = useRef(0);

  const prev = () =>
    setI((curr) => (curr > 0 ? curr - 1 : loop ? urls.length - 1 : curr));
  const next = () =>
    setI((curr) => (curr < urls.length - 1 ? curr + 1 : loop ? 0 : curr));

  const onPointerDown = (e: PointerEvent) => {
    startX.current = e.clientX;
    deltaX.current = 0;
    (e.target as Element).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: PointerEvent) => {
    if (startX.current == null || !trackRef.current) return;
    deltaX.current = e.clientX - startX.current;
    const percent =
      -i * 100 + (deltaX.current / trackRef.current.clientWidth) * 100;
    trackRef.current.style.transform = `translateX(${percent}%)`;
  };

  const onPointerUp = () => {
    if (!trackRef.current) return;
    const threshold = trackRef.current.clientWidth * 0.15; // 15%
    if (deltaX.current > threshold) prev();
    else if (deltaX.current < -threshold) next();

    trackRef.current.style.transform = `translateX(${-i * 100}%)`;
    startX.current = null;
    deltaX.current = 0;
  };

  useEffect(() => {
    onIndexChange?.(i);
  }, [i, onIndexChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [urls?.length, loop]);

  if (!urls || urls.length === 0) return null;

  return (
    <div
      className={cn("relative select-none", className)}
      role="group"
      aria-roledescription="carousel"
      aria-label="Galería de imágenes"
    >
      <div
        className={cn("overflow-hidden bg-black/5", panelClassName)}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <div
          ref={trackRef}
          className="flex h-full transition-transform duration-300 ease-out"
          style={{ transform: `translateX(${-i * 100}%)` }}
        >
          {urls.map((src, idx) => (
            <div
              key={idx}
              className="relative w-full shrink-0 grow-0 basis-full"
            >
              <img
                src={src}
                alt={`Imagen ${idx + 1} de ${urls.length}`}
                className="relative z-10 h-full w-full object-cover transition-opacity duration-100"
                loading="lazy"
              />
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={prev}
          aria-label="Anterior"
          className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 px-2 py-6 text-white hover:bg-black/60 focus:outline-none"
        >
          <Icon name={"chevronLeft"} className="" />
        </button>

        <button
          type="button"
          onClick={next}
          aria-label="Siguiente"
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 px-2 py-6  text-white hover:bg-black/60 focus:outline-none"
        >
          <Icon name={"chevronRight"} className="" />
        </button>
      </div>

      {showIndicators && (
        <div className="mt-2 flex items-center justify-center gap-2">
          {urls.map((_, idx) => (
            <button
              key={idx}
              aria-label={`Ir a la imagen ${idx + 1}`}
              className={[
                "h-2 w-2 rounded-full",
                i === idx ? "bg-gray-900" : "bg-gray-300 hover:bg-gray-400",
              ].join(" ")}
              onClick={() => setI(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;
